/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useState} from 'react';
import {useIntl} from 'react-intl';
import PropTypes from 'prop-types';

import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Stack,
  Text,
  useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';
import {PlusIcon, MinusIcon} from '@amblique/king-living/app/components/icons';

const InformationAccordion = ({product, isSimpleProduct = true}) => {
  const styles = useMultiStyleConfig('ProductDetail', {});
  const [expandedIndices, setExpandedIndices] = useState([]);
  const {formatMessage} = useIntl();

  // Check what specification sections have content
  const hasProductInformation = Boolean(product?.longDescription);
  const hasWhatsIncluded = Boolean(product?.c_whatsIncluded);
  const hasMeasurements =
    Boolean(product?.dimHeight) ||
    Boolean(product?.dimWidth) ||
    Boolean(product?.dimDepth);

  // Build array of section indices
  // Specifications sections come first (if they exist), then original sections
  const availableSections = [];
  let sectionIndex = 0;

  // Specifications sections (dynamic - only if content exists)
  if (hasProductInformation) {
    availableSections.push(sectionIndex);
    sectionIndex++;
  }
  if (hasWhatsIncluded) {
    availableSections.push(sectionIndex);
    sectionIndex++;
  }
  if (hasMeasurements) {
    availableSections.push(sectionIndex);
    sectionIndex++;
  }

  // Original sections (always shown)
  const productDetailIndex = sectionIndex++;
  const sizeFitIndex = sectionIndex++;
  const reviewsIndex = sectionIndex++;
  const questionsIndex = sectionIndex++;

  // All sections for expand/collapse all functionality
  const allSections = [
    ...availableSections,
    productDetailIndex,
    sizeFitIndex,
    reviewsIndex,
    questionsIndex
  ];

  const handleExpandAll = () => {
    if (expandedIndices.length === allSections.length) {
      // Collapse all
      setExpandedIndices([]);
    } else {
      // Expand all - expand specifications sections and original sections
      setExpandedIndices(allSections);
    }
  };

  const isAllExpanded = expandedIndices.length === allSections.length;

  return (
    <Box id="specifications" {...styles.specificationsContainer}>
      <Box {...styles.specificationsInnerBox}>
        {/* Header with Expand All button */}
        <Box {...styles.specificationsHeader}>
          <Text {...styles.specificationsTitle}>
            {formatMessage({
              defaultMessage: 'Specifications',
              id: 'product_detail.tab.title.specifications',
            })}
          </Text>
          {!isSimpleProduct && (
            <Button
              aria-label={isAllExpanded ? 'Collapse All' : 'Expand All'}
              variant="ghost"
              {...styles.specificationsExpandAllButton}
              onClick={handleExpandAll}
            >
              <Text {...styles.specificationsExpandAllText}>
                Expand all
              </Text>
              <Box {...styles.specificationsExpandAllIconBox}>
                {isAllExpanded ? (
                  <MinusIcon height="100%" width="100%" />
                ) : (
                  <PlusIcon height="100%" width="100%" />
                )}
              </Box>
            </Button>)}
        </Box>

        <Accordion
          {...styles.specificationsAccordion}
          index={expandedIndices}
          allowMultiple
          onChange={setExpandedIndices}
        >
          {/* Product Information */}
          {hasProductInformation && (
            <AccordionItem {...styles.specificationsAccordionItem}>
              {({isExpanded}) => (
                <>
                  <h2>
                    <AccordionButton {...styles.specificationsAccordionButton}>
                      <Box {...styles.specificationsSectionTitle}>
                        Product Information
                      </Box>
                      <Box {...styles.specificationsIconBox}>
                        {isExpanded ? (
                          <MinusIcon height="100%" width="100%" />
                        ) : (
                          <PlusIcon height="100%" width="100%" />
                        )}
                      </Box>
                    </AccordionButton>
                  </h2>
                  <AccordionPanel {...styles.specificationsAccordionPanel}>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: product?.longDescription,
                      }}
                    />
                  </AccordionPanel>
                </>
              )}
            </AccordionItem>
          )}

          {/* What's Included */}
          {hasWhatsIncluded && (
            <AccordionItem {...styles.specificationsAccordionItem}>
              {({isExpanded}) => (
                <>
                  <h2>
                    <AccordionButton {...styles.specificationsAccordionButton}>
                      <Box {...styles.specificationsSectionTitle}>
                        What&apos;s Included
                      </Box>
                      <Box {...styles.specificationsIconBox}>
                        {isExpanded ? (
                          <MinusIcon height="100%" width="100%" />
                        ) : (
                          <PlusIcon height="100%" width="100%" />
                        )}
                      </Box>
                    </AccordionButton>
                  </h2>
                  <AccordionPanel {...styles.specificationsAccordionPanel}>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: product?.c_whatsIncluded,
                      }}
                    />
                  </AccordionPanel>
                </>
              )}
            </AccordionItem>
          )}

          {/* Measurements */}
          {hasMeasurements && (
            <AccordionItem {...styles.specificationsAccordionItem}>
              {({isExpanded}) => (
                <>
                  <h2>
                    <AccordionButton {...styles.specificationsAccordionButton}>
                      <Box {...styles.specificationsSectionTitle}>
                        Measurements
                      </Box>
                      <Box {...styles.specificationsIconBox}>
                        {isExpanded ? (
                          <MinusIcon height="100%" width="100%" />
                        ) : (
                          <PlusIcon height="100%" width="100%" />
                        )}
                      </Box>
                    </AccordionButton>
                  </h2>
                  <AccordionPanel {...styles.specificationsAccordionPanel}>
                    <Stack spacing={3}>
                      {product?.dimHeight && (
                        <Box>
                          <Text as="span" fontWeight="bold">
                            Height:
                          </Text>{' '}
                          <Text as="span">{product.dimHeight}</Text>
                        </Box>
                      )}
                      {product?.dimWidth && (
                        <Box>
                          <Text as="span" fontWeight="bold">
                            Width:
                          </Text>{' '}
                          <Text as="span">{product.dimWidth}</Text>
                        </Box>
                      )}
                      {product?.dimDepth && (
                        <Box>
                          <Text as="span" fontWeight="bold">
                            Depth:
                          </Text>{' '}
                          <Text as="span">{product.dimDepth}</Text>
                        </Box>
                      )}
                    </Stack>
                  </AccordionPanel>
                </>
              )}
            </AccordionItem>
          )}
        </Accordion>
      </Box>
    </Box>
  );
};

InformationAccordion.propTypes = {
  product: PropTypes.object,
  isSimpleProduct: PropTypes.bool,
};

export default InformationAccordion;
